import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import { findIndex, get, isEmpty } from "lodash";
import moment from "moment";
import { getCreatedAtDescriptionFromDate } from "@sonder/ui/source/styled/components/Comment";
import { LastViewedAt } from "@sonder/ui/source/styled/components/LastReadAt";
import { CircleButton } from "@sonder/ui/source/styled/fragments/CircleButton";
import { Padder } from "@sonder/ui/source/styled/fragments/Padder";
import { PanelCard } from "@sonder/ui/source/styled/fragments/PanelCard";
import { PanelContent } from "@sonder/ui/source/styled/layouts/PanelContent";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import { THIRTY_SECONDS } from "core/constants";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { usePageVisibilityPolling } from "core/hooks/usePageVisibilityPolling";
import { useCreateTaskComment } from "core/queries/CreateTaskComment";
import { TaskData, TaskSubjectType } from "core/queries/GetTask";
import {
  ActivityTypenames,
  GetTaskCommentsResponse,
  TaskAction,
  TaskActivity,
  TaskComment,
  useGetTaskCommentsQuery,
} from "core/queries/GetTaskComments";
import { useUpdateTask } from "core/queries/UpdateTask";
import {
  DeleteCommentOptions,
  useUpdateTaskComment,
} from "core/queries/UpdateTaskComment";
import { ActivityContentLoader } from "tasks/components/TaskPanel/Activity/Activity.loader";
import { AddActionModal } from "tasks/components/TaskPanel/Activity/AddActionModal";
import { CommentInput } from "tasks/components/TaskPanel/Activity/CommentInput";
import { FollowTaskLink } from "tasks/components/TaskPanel/Activity/FollowTaskLink";
import { SalesforceCard } from "tasks/components/TaskPanel/Activity/SalesforceCard";
import { TaskAction as Action } from "tasks/components/TaskPanel/Activity/TaskAction";
import { TaskActivity as GenericActivity } from "tasks/components/TaskPanel/Activity/TaskActivity";
import { TaskComment as Comment } from "tasks/components/TaskPanel/Activity/TaskComment";
import { UploadTaskPhotoModal } from "tasks/components/TaskPanel/Activity/UploadTaskPhotoModal";

interface CreateCommentArgs {
  htmlBody: string;
  plainBody: string;
  mentions: string[];
}

// Helpers
const getComments = (
  data: GetTaskCommentsResponse | undefined,
  deleted: string[],
) => {
  const allComments = get(
    data,
    "task.taskFeedItemConnections.taskFeedItems",
    [],
  ) as TaskComment[];
  return allComments.filter(
    comment => isEmpty(comment.deletedAt) || deleted.includes(comment.id),
  );
};

const isAddActionModalVisible = (task: TaskData) => {
  const { subjectType } = task;
  return (
    subjectType === TaskSubjectType.InternalHold ||
    subjectType === TaskSubjectType.Inquiry ||
    subjectType === TaskSubjectType.LeaseInquiry ||
    subjectType === TaskSubjectType.Unit
  );
};

const safeScrollTo = (target: HTMLElement, left: number, top: number) => {
  const hasNativeScrollToSupport =
    "scrollBehavior" in document.documentElement.style;
  if (hasNativeScrollToSupport) target.scrollTo(left, top);
  else {
    target.scrollLeft = left;
    target.scrollTop = top;
  }
};

// Hooks
const useScrollToBottom = () => {
  const scrollRef = useRef<HTMLElement>(null);
  const [scrollFlag, setScrollFlag] = useState(false);
  useEffect(() => {
    if (scrollFlag && scrollRef.current) {
      safeScrollTo(scrollRef.current, 0, scrollRef.current.scrollHeight);
      setScrollFlag(false);
    }
  }, [scrollFlag, scrollRef.current]);

  return { scrollRef, scrollToBottom: () => setScrollFlag(true) };
};

const useMarkTaskViewedOnMount = (taskId: string, shouldMark: boolean) => {
  const [onUpdateTask] = useUpdateTask({});

  useEffect(() => {
    if (shouldMark)
      onUpdateTask({
        variables: {
          taskId,
          lastViewedAt: moment().toISOString(),
        },
      });
  }, [taskId, shouldMark]);
};

const useLastViewedAt = (task: TaskData) => {
  const [lastViewedAt, setLastViewedAt] = useState<string | null>(null);

  useEffect(
    () => setLastViewedAt(get(task, "taskFeedItemConnections.lastViewedAt", 0)),
    [task.id],
  );

  return { lastViewedAt, setLastViewedAt };
};

const useTaskComments = (
  taskId: string,
  pollInterval: number = THIRTY_SECONDS,
) => {
  const [submitting, setSubmitting] = useState(false);
  const [deletedComments, setDeletedComments] = useState<string[]>([]);
  const {
    data,
    error,
    loading: loadingComments,
    stopPolling,
    startPolling,
    refetch,
  } = useGetTaskCommentsQuery({
    variables: { taskId },
    pollInterval,
  });

  usePageVisibilityPolling({
    pollInterval,
    refetch,
    variables: { taskId },
    startPolling,
    stopPolling,
  });

  const [onCreateTaskComment] = useCreateTaskComment({
    refetchQueries: ["GetTaskComments"],
    awaitRefetchQueries: true,
  });

  const [onUpdateTaskComment] = useUpdateTaskComment({
    refetchQueries: ["GetTaskComments"],
  });

  return {
    comments: getComments(data, deletedComments),
    loading: !data || !data.task || (!data.task && loadingComments),
    submitting,
    error,
    // Actions
    createComment: async ({
      htmlBody,
      plainBody,
      mentions,
    }: CreateCommentArgs) => {
      setSubmitting(true);
      await onCreateTaskComment({
        variables: {
          taskId,
          plainBody,
          htmlBody,
          mentions,
          markAllRead: true,
        },
      });
      setSubmitting(false);
    },
    deleteComment: async (options: DeleteCommentOptions) => {
      const { id: commentId, deleted, richBody, mentions } = options;
      await onUpdateTaskComment({
        variables: { commentId, deleted, richBody, mentions },
      });
      setDeletedComments([...deletedComments, commentId]);
    },
  };
};

// Fragments
const TaskCreatedAt = styled.span`
  color: ${colors.gray6};
  font-size: 0.85rem;
`;
const PanelContentLoader = styled(PanelContent)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

interface LastViewedAt {
  __typename: ActivityTypenames.LastViewedAt;
  createdAt: string;
  id: string;
}

type ActivityType = TaskAction | TaskComment | TaskActivity | LastViewedAt;

interface ActivityProps {
  task: TaskData;
}
export const Activity = ({ task }: ActivityProps) => {
  const errorReporter = useErrorReporterFiltered();
  const { track } = useSegment();
  const { state: authState } = useAuthStore();

  const { comments, error, loading, submitting, createComment, deleteComment } =
    useTaskComments(task.id);
  const { scrollRef, scrollToBottom } = useScrollToBottom();
  // TLDR: Mutate and Freeze the task as "lastViewed" on Mount, only update on post / remount.
  useMarkTaskViewedOnMount(task.id, !(loading || error));
  const { lastViewedAt: cachedLastViewedAt, setLastViewedAt } =
    useLastViewedAt(task);
  const [isAddActionModalOpen, setAddActionModalOpen] = useState(false);
  const [isUploadTaskPhotoOpen, setUploadTaskPhotoOpen] = useState(false);
  const commentInput = (
    <CommentInput
      disabled={!!error || loading}
      submitting={submitting}
      onPostComment={async nextEditorContent => {
        try {
          await createComment(nextEditorContent);
          // Update the mount on post, we could be more precise, but it's ok.
          setLastViewedAt(moment().toISOString());
          scrollToBottom();
        } catch (e) {
          errorReporter.notify(e);
        }
      }}
    >
      {isAddActionModalVisible(task) && (
        <>
          <CircleButton
            backgroundVariant="light"
            onClick={() => {
              setAddActionModalOpen(true);
              track({
                eventName: "task_panel",
                action: "click",
                element: "add_action_modal_open",
                props: {
                  taskId: task.id,
                },
              });
            }}
          >
            <AddCircleOutlineIcon />
          </CircleButton>
          <AddActionModal
            isOpen={isAddActionModalOpen}
            task={task}
            onClose={() => {
              setAddActionModalOpen(false);
              track({
                eventName: "task_panel",
                action: "click",
                element: "add_action_modal_close",
                props: {
                  taskId: task.id,
                },
              });
            }}
          />
        </>
      )}
      <>
        <CircleButton
          backgroundVariant="light"
          onClick={() => {
            setUploadTaskPhotoOpen(true);
            track({
              eventName: "task_panel",
              action: "click",
              element: "upload_task_photo_open",
              props: {
                taskId: task.id,
              },
            });
          }}
        >
          <CameraAltIcon />
        </CircleButton>
        <UploadTaskPhotoModal
          isOpen={isUploadTaskPhotoOpen}
          task={task}
          onClose={() => {
            setUploadTaskPhotoOpen(false);
            track({
              eventName: "task_panel",
              action: "click",
              element: "upload_task_photo_close",
              props: {
                taskId: task.id,
              },
            });
          }}
        />
      </>
      <FollowTaskLink task={task} />
    </CommentInput>
  );

  if (loading || error)
    return (
      <PanelContentLoader>
        {error ? "Error" : <ActivityContentLoader />}
      </PanelContentLoader>
    );

  const userId = get(authState, "user.id", null);

  const lastViewedAt: LastViewedAt = {
    __typename: ActivityTypenames.LastViewedAt,
    createdAt: moment(cachedLastViewedAt || 0).toISOString(),
    id: ActivityTypenames.LastViewedAt,
  };
  const attachments = (task?.attachments || []).map(attachment => ({
    attachments: [attachment],
    author: attachment.user,
    body: "Attachment added",
    createdAt: attachment.createdAt,
    id: attachment.id,
  }));
  const allActivities = [...comments, ...attachments].sort((a, b) =>
    a.createdAt < b.createdAt ? -1 : 1,
  ) as ActivityType[];
  const indexOfLastViewedAt = findIndex(allActivities, (c: ActivityType) => {
    const authorId = get(c, "author.id");
    return c.createdAt > lastViewedAt.createdAt && authorId !== userId;
  });

  if (indexOfLastViewedAt >= 0 && indexOfLastViewedAt < allActivities.length)
    allActivities.splice(indexOfLastViewedAt, 0, lastViewedAt);

  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    <>
      <PanelContent ref={scrollRef}>
        <SalesforceCard task={task} />
        <PanelCard>
          <TaskCreatedAt>
            Task created
            {task.author ? ` by ${task.author.name} ` : " "}
            at {getCreatedAtDescriptionFromDate(task.createdAt)}
          </TaskCreatedAt>
        </PanelCard>
        {allActivities.map((activity: ActivityType) => {
          const activityId = activity.id;
          switch (activity.__typename) {
            case ActivityTypenames.TaskAction:
              return (
                <Action
                  action={activity}
                  key={activityId}
                  loggedInUserId={userId}
                  taskId={task.id}
                />
              );
            case ActivityTypenames.LastViewedAt:
              return <LastViewedAt key={activityId}>New Activity</LastViewedAt>;
            case ActivityTypenames.TaskActivity:
              return <GenericActivity activity={activity} key={activityId} />;
            case ActivityTypenames.TaskComment:
            default:
              return (
                <Comment
                  comment={activity as TaskComment}
                  deleteComment={deleteComment}
                  key={activityId}
                  loggedInUserId={userId}
                />
              );
          }
        })}
        <Padder />
      </PanelContent>
      {commentInput}
    </>
  );
};
